// Esqueletos de carga, uno por tipo de bloque, con el mismo espaciado, radio
// y diseño responsivo (consultas de contenedor) que el bloque real: al llegar
// los datos, el contenido toma su lugar sin saltos. Lo usan el sitio público
// (con la forma que manda `getSite`) y la vista previa del editor (con la
// copia local).

import { Fragment, type ReactNode } from "react";
import type { SiteSkeletonBlock } from "@/convex/lib/schema/site_shape";
import { Skeleton } from "@/components/ui/skeleton";

/** Un bloque para el esqueleto: su forma y, si se sabe, si lleva título. */
export type BlockSkeletonShape = SiteSkeletonBlock & { titled?: boolean };

const CARD = "rounded-lg border border-border bg-card";

function Title({ show }: { show?: boolean }) {
  return show ? <Skeleton className="h-6 w-40" /> : null;
}

/** Tarjeta de un registro (lista en tarjetas, o la tabla en el celular). */
function RecordCardSkeleton() {
  return (
    <li className={`${CARD} p-4`}>
      <Skeleton className="h-5 w-1/2" />
      <div className="mt-2 space-y-2">
        <div className="flex justify-between gap-3">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-24" />
        </div>
        <div className="flex justify-between gap-3">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-20" />
        </div>
      </div>
    </li>
  );
}

function TableSkeleton({ columns, rows }: { columns: number; rows: number }) {
  const cols = Array.from({ length: columns }, (_, i) => i);
  return (
    <div className={`hidden overflow-hidden ${CARD} @min-[48rem]:block`}>
      <div className="flex gap-6 border-b border-border bg-muted/60 px-4 py-3">
        {cols.map((c) => (
          <Skeleton key={c} className="h-4 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex gap-6 border-b border-border px-4 py-3 last:border-0">
          {cols.map((c) => (
            <Skeleton key={c} className="h-4 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

function ShapeOf({ block }: { block: BlockSkeletonShape }): ReactNode {
  switch (block.type) {
    case "heading":
      return <Skeleton className="h-7 w-2/3 max-w-sm @min-[40rem]:h-8" />;
    case "text":
      return (
        <div className="space-y-2.5 py-1">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-11/12" />
          <Skeleton className="h-4 w-3/5" />
        </div>
      );
    case "stat":
      return (
        <div className={`${CARD} p-4`}>
          <Skeleton className="h-4 w-24" />
          <Skeleton className="mt-2 h-8 w-16" />
        </div>
      );
    case "list":
      return (
        <div className="space-y-3">
          <Title show={block.titled} />
          {block.variant === "cards" ? (
            <ul className="grid gap-3 @min-[40rem]:grid-cols-2 @min-[64rem]:grid-cols-3">
              <RecordCardSkeleton />
              <RecordCardSkeleton />
              <RecordCardSkeleton />
            </ul>
          ) : (
            <>
              <TableSkeleton columns={4} rows={5} />
              <ul className="space-y-2 @min-[48rem]:hidden">
                <RecordCardSkeleton />
                <RecordCardSkeleton />
                <RecordCardSkeleton />
              </ul>
            </>
          )}
        </div>
      );
    case "grid":
      return (
        <div className="space-y-3">
          <Title show={block.titled} />
          <TableSkeleton columns={5} rows={4} />
          {/* Celular: una columna a la vez, como la grilla real. */}
          <div className="space-y-3 @min-[48rem]:hidden">
            <div className="flex gap-2 overflow-hidden pb-1">
              <Skeleton className="h-9 w-20 shrink-0 rounded-full" />
              <Skeleton className="h-9 w-20 shrink-0 rounded-full" />
              <Skeleton className="h-9 w-20 shrink-0 rounded-full" />
            </div>
            <ul className={`divide-y divide-border ${CARD}`}>
              {[0, 1, 2, 3].map((r) => (
                <li key={r} className="flex gap-3 px-4 py-3">
                  <Skeleton className="h-4 w-28 shrink-0" />
                  <Skeleton className="h-4 flex-1" />
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    case "chart":
      return (
        <div className="space-y-3">
          <Title show={block.titled} />
          <div className={`space-y-3 ${CARD} p-4`}>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-3 w-14" />
            </div>
            <Skeleton className="h-[220px] w-full" />
            <Skeleton className="h-4 w-28" />
          </div>
        </div>
      );
    case "picker":
      return (
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <div className="flex flex-wrap gap-2">
            <Skeleton className="h-9 w-24 rounded-full" />
            <Skeleton className="h-9 w-28 rounded-full" />
            <Skeleton className="h-9 w-20 rounded-full" />
          </div>
        </div>
      );
    case "form":
      return (
        <div className={`space-y-4 ${CARD} p-4 @min-[40rem]:p-5`}>
          <Title show={block.titled} />
          {Array.from({ length: Math.max(1, block.fields) }, (_, i) => (
            <div key={i} className="space-y-1.5">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-[46px] w-full" />
            </div>
          ))}
          <Skeleton className="h-11 w-full @min-[40rem]:w-32" />
        </div>
      );
    case "section":
      return (
        <div className={CARD}>
          <div className="flex items-center gap-2 px-4 py-3">
            <Skeleton className="size-3.5 shrink-0 rounded-sm" />
            <Skeleton className="h-4 w-40" />
          </div>
          {block.open && block.blocks.length > 0 && (
            <div className="@container border-t border-border p-4 @min-[40rem]:p-5">
              <SiteBlocksSkeleton blocks={block.blocks} />
            </div>
          )}
        </div>
      );
    case "pdf":
      return (
        <div className={`space-y-4 ${CARD} p-4 @min-[40rem]:p-5`}>
          <div className="flex items-start gap-3">
            <Skeleton className="mt-0.5 size-5 shrink-0" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-56 max-w-full" />
            </div>
          </div>
          <Skeleton className="h-11 w-full @min-[40rem]:w-36" />
        </div>
      );
  }
}

/** Esqueleto de un bloque. */
export function BlockSkeleton({ block }: { block: BlockSkeletonShape }) {
  return (
    <div data-testid="site-block-skeleton" data-type={block.type} aria-hidden>
      <ShapeOf block={block} />
    </div>
  );
}

/**
 * Esqueletos de una página, en orden; los números seguidos van en la misma
 * grilla que `SiteBlocks`. `wrap` (solo el editor) envuelve cada uno en su
 * marco.
 */
export function SiteBlocksSkeleton<T extends BlockSkeletonShape>({
  blocks,
  wrap,
}: {
  blocks: readonly T[];
  wrap?: (block: T, index: number, node: ReactNode) => ReactNode;
}) {
  const groups: Array<{ index: number; block: T } | Array<{ index: number; block: T }>> = [];
  blocks.forEach((block, index) => {
    const last = groups[groups.length - 1];
    if (block.type === "stat" && Array.isArray(last)) last.push({ index, block });
    else groups.push(block.type === "stat" ? [{ index, block }] : { index, block });
  });
  const one = ({ index, block }: { index: number; block: T }) => {
    const node = <BlockSkeleton block={block} />;
    return <Fragment key={index}>{wrap ? wrap(block, index, node) : node}</Fragment>;
  };
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Cargando" data-testid="site-blocks-skeleton">
      {groups.map((group) =>
        Array.isArray(group) ? (
          <div key={group[0].index} className="grid grid-cols-2 gap-3 @min-[40rem]:grid-cols-3">
            {group.map(one)}
          </div>
        ) : (
          <div key={group.index}>{one(group)}</div>
        ),
      )}
    </div>
  );
}
